// Compact, honest compatibility summary of the chosen platform for THIS
// creative's product (and format), exactly as the server assessed it with
// InteractAds' evidence. Never "100% compatible", never "certified by" a
// platform: every claim is scoped and attributed. Details (evidence,
// scope, limitations) live behind a disclosure, not in the main flow.
import { useQueryClient } from "@tanstack/react-query";
import { Ban, CircleCheck, CircleHelp, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { keys } from "@/api/hooks";
import type { CompatibilityAssessment, CompatibilityStatus } from "@/api/types";
import { formatDateTime } from "@/lib/format";
import { COMPATIBILITY_STATES, EVIDENCE_SOURCES, PLATFORM_TYPE_LABELS, PRODUCTS, displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { notifyError } from "../common/notify";
import { requestReview } from "./queries";

const ICONS: Record<CompatibilityStatus, typeof CircleCheck> = {
  VERIFIED: CircleCheck,
  LIMITED: TriangleAlert,
  UNVERIFIED: CircleHelp,
  UNSUPPORTED: Ban,
};

const TEXT_TONE: Record<CompatibilityStatus, string> = {
  VERIFIED: "text-success",
  LIMITED: "text-warning",
  UNVERIFIED: "text-muted",
  UNSUPPORTED: "text-danger",
};

/** A status line: icon + words (never color alone). */
export function StatusLine({ status, children }: { status: CompatibilityStatus; children: string }) {
  const Icon = ICONS[status];
  return (
    <span className="flex items-start gap-1.5 text-sm">
      <Icon className={cn("mt-0.5 size-4 shrink-0", TEXT_TONE[status])} aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}

/** Short label of a Distribution's overall compatibility (lists, headers). */
export function CompatibilityBadge({ status }: { status: CompatibilityStatus }) {
  const Icon = ICONS[status];
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted">
      <Icon className={cn("size-3.5", TEXT_TONE[status])} aria-hidden="true" />
      {COMPATIBILITY_STATES[status].label}
    </span>
  );
}

export function CompatibilitySummary({
  orgId,
  assessment,
  canRequestReview,
}: {
  orgId: string;
  assessment: CompatibilityAssessment;
  canRequestReview: boolean;
}) {
  const queryClient = useQueryClient();
  const [requesting, setRequesting] = useState(false);
  const a = assessment;
  const product = PRODUCTS[a.product].label;
  const format = a.format ? displayFormat(a.format) : null;
  const custom = a.platform.kind === "custom";
  const evidence = [...a.capability.evidence, ...(a.formatAssessment?.evidence ?? [])];
  const limitations = [a.capability.limitations, a.formatAssessment?.limitations].filter(
    (l): l is string => typeof l === "string" && l !== ""
  );

  const ask = async () => {
    setRequesting(true);
    try {
      await requestReview(orgId, { kind: a.platform.kind, id: a.platform.id }, a.product);
      await queryClient.invalidateQueries({ queryKey: [...keys.org(orgId), "platform-compatibility"] });
      await queryClient.invalidateQueries({ queryKey: [...keys.org(orgId), "distribution"] });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <section
      aria-label={`Compatibilidad con ${a.platform.name}`}
      className="grid gap-2 rounded-md border border-line bg-surface p-3"
      data-testid="compatibility-summary"
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-semibold">{a.platform.name}</span>
        <span className="text-xs text-muted">
          {custom ? "Plataforma personalizada" : PLATFORM_TYPE_LABELS[a.platform.platformType]}
          {a.platform.mappedTo ? ` · revisada por InteractAds como ${a.platform.mappedTo.name}` : ""}
        </span>
      </div>
      <div className="grid gap-1">
        <StatusLine status={a.capability.status}>
          {a.basis === "CUSTOM_UNVERIFIED"
            ? `${product}: compatibilidad aún no verificada por InteractAds`
            : `${product}: ${COMPATIBILITY_STATES[a.capability.status].label}`}
        </StatusLine>
        {a.formatAssessment && format ? (
          <StatusLine status={a.formatAssessment.status}>
            {COMPATIBILITY_STATES[a.formatAssessment.status].formatLabel(format)}
          </StatusLine>
        ) : null}
      </div>

      {a.overall === "UNSUPPORTED" ? (
        <p className="text-sm text-danger" role="alert">
          En nuestra prueba, {a.platform.name} no aceptó {format ? `el formato ${format} para ` : ""}
          {product}. Elige otro formato u otra plataforma.
        </p>
      ) : a.overall === "LIMITED" ? (
        <p className="text-sm">Funciona con limitaciones. Revísalas antes de continuar.</p>
      ) : a.overall === "UNVERIFIED" ? (
        <p className="text-sm text-muted">
          InteractAds aún no ha verificado {custom && !a.platform.mappedTo ? "esta plataforma" : "esta combinación"}.
          Puedes continuar: te recomendamos probar el Tag en la plataforma antes de lanzar tu campaña.
        </p>
      ) : (
        <p className="text-sm text-muted">Según nuestras pruebas, puedes usar el Tag en esta plataforma.</p>
      )}

      {limitations.length > 0 ? (
        <ul className="grid gap-0.5 text-sm">
          {limitations.map((l) => (
            <li key={l}>· {l}</li>
          ))}
        </ul>
      ) : null}

      {a.overall !== "VERIFIED" && canRequestReview ? (
        a.reviewRequest ? (
          <p className="text-xs text-muted" data-testid="review-requested">
            {a.reviewRequest.status === "OPEN"
              ? `Revisión de compatibilidad solicitada el ${formatDateTime(a.reviewRequest.createdAt)}.`
              : "InteractAds ya revisó esta plataforma."}
          </p>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            className="justify-self-start"
            disabled={requesting}
            onClick={() => void ask()}
          >
            Solicitar revisión de compatibilidad
          </Button>
        )
      ) : null}

      {evidence.length > 0 ? (
        <details className="group text-sm">
          <summary className="cursor-pointer select-none text-xs font-medium text-brand underline-offset-2 hover:underline">
            Ver evidencia y alcance
          </summary>
          <div className="mt-2">
            <ul className="grid gap-2 text-sm">
              {evidence.map((e) => (
                <li key={`${e.recordedAt}-${e.summary}`} className="grid gap-0.5">
                  <span className="text-xs font-medium">
                    {EVIDENCE_SOURCES[e.sourceType]} · {COMPATIBILITY_STATES[e.status].label}
                    {e.observedOn ? ` · ${e.observedOn}` : ""}
                  </span>
                  <span className="text-muted">{e.summary}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-muted">
              La evidencia describe nuestras pruebas: no cubre toda cuenta, flujo de compra ni cambios futuros de la
              plataforma.
            </p>
          </div>
        </details>
      ) : null}
    </section>
  );
}
